import type { ApiResponse, RequestBillingEmailCodeResult } from '@fluenceur/types';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import api from '@/libs/axiosInstance';
import { z } from 'zod';
import { BillingTargetChangedError, useBillingActionScope } from './use-billing-action-scope';
import { toaster } from '@/components/ui/toaster';
import { useTranslation } from 'react-i18next';
import { billingEmailError } from '@/pages/onboarding/billing/billing-email-error';

export const BILLING_EMAIL_REQUEST_MUTATION_KEY = ['billingEmailRequest'] as const;

const resultSchema = z
  .object({
    email: z.string().email(),
    expiresAt: z.string().datetime({ offset: true }),
    retryAfterSeconds: z.number().finite().nonnegative(),
  })
  .strict();

export function useRequestBillingEmailVerificationAction() {
  const { t } = useTranslation();
  const { scope, assertCurrent } = useBillingActionScope();
  return useMutation<RequestBillingEmailCodeResult, AxiosError<ApiResponse> | Error, void>({
    mutationKey: [...BILLING_EMAIL_REQUEST_MUTATION_KEY, scope.organizationId, scope.brandId],
    retry: false,
    mutationFn: async () => {
      assertCurrent(scope);
      const { data } = await api.post<ApiResponse<unknown>>(
        '/dashboard/billing/email-verification/request'
      );
      assertCurrent(scope);
      const result = resultSchema.safeParse(data.data);
      if (data.success !== true || !result.success) throw new Error('Invalid email code response');
      return result.data;
    },
    onError: (error) => {
      if (error instanceof BillingTargetChangedError) return;
      toaster.error({
        title: t('common.error'),
        description: t(billingEmailError(error, 'signup.verify.errors.sendFailed')),
      });
    },
  });
}
